import React from 'react';
import { ProgressBarCompact } from './ProgressBar';
import { categoryLabel } from '../constants/eventCategories';

/**
 * Event list item card component
 * Design: High-contrast borders, rectangular, monospace text
 */
export default function EventCard({
  event,
  onClick,
  showProgress = true,
  showStatus = true,
  className = '',
}) {
  const {
    name,
    date,
    location,
    posterUrl,
    fundingCurrent = 0,
    fundingGoal = 0,
    status = 'draft',
    applicationCount = 0,
    category,
    categories,
    customCategory,
    sponsorName,
    isFeatured,
    isExternalTicketing,
  } = event;

  // Chips: multi-category if present, else the single primary. "other" shows the
  // custom label when one was given, otherwise nothing.
  const catChips = (categories && categories.length ? categories : (category ? [category] : []))
    .map((slug) => (slug === 'other' ? (customCategory || null) : categoryLabel(slug)))
    .filter(Boolean)
    .slice(0, 3);

  const statusColors = {
    draft: 'bg-gray-200 text-gray-700',
    live: 'bg-green-500 text-white',
    full: 'bg-yellow-500 text-black',
    ended: 'bg-black text-white',
    cancelled: 'bg-red-500 text-white',
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).toUpperCase();
  };

  return (
    <div
      onClick={onClick}
      className={`
        border-2 border-black bg-white
        cursor-pointer
        hover:bg-gray-50
        transition-colors duration-200
        ${className}
      `}
    >
      <div className="flex">
        {/* Poster thumbnail */}
        {posterUrl && (
          <div className="w-24 h-24 flex-shrink-0 border-r-2 border-black overflow-hidden">
            <img
              src={posterUrl}
              alt={name}
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Event info */}
        <div className="flex-1 p-4">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
              {name}
            </h3>
            {showStatus && (
              <span className={`px-2 py-1 font-mono text-xs font-semibold uppercase tracking-wider ${statusColors[status]}`}>
                {status}
              </span>
            )}
          </div>

          <div className="space-y-1 mb-3">
            <p className="font-mono text-xs text-gray-600 uppercase tracking-wider">
              {formatDate(date)}
            </p>
            {location && (
              <p className="font-mono text-xs text-gray-600 uppercase tracking-wider">
                {location}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              {catChips.map((label) => (
                <span key={label} className="px-1.5 py-0.5 border border-black font-mono text-[10px] uppercase tracking-wider">
                  {label}
                </span>
              ))}
              {isFeatured && (
                <span className="px-1.5 py-0.5 bg-black text-white font-mono text-[10px] uppercase tracking-wider">
                  Featured
                </span>
              )}
              {isExternalTicketing && (
                <span className="px-1.5 py-0.5 bg-black text-white font-mono text-[10px] uppercase tracking-wider">
                  External Tickets
                </span>
              )}
              {sponsorName && (
                <span className="font-mono text-[10px] text-gray-600 uppercase tracking-wider">
                  Presented by <span className="font-bold text-black">{sponsorName}</span>
                </span>
              )}
            </div>
          </div>

          {showProgress && !isExternalTicketing && fundingGoal > 0 && (
            <div className="mb-2">
              <ProgressBarCompact current={fundingCurrent} target={fundingGoal} />
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="font-mono text-xs text-gray-500 uppercase tracking-wider">
              {applicationCount} APPLICATIONS
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact event card for smaller displays
 */
export function EventCardCompact({
  event,
  onClick,
  className = '',
}) {
  const { name, date, status = 'draft' } = event;

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }).toUpperCase();
  };

  return (
    <div
      onClick={onClick}
      className={`
        px-4 py-3
        border-2 border-black bg-white
        cursor-pointer
        hover:bg-gray-50
        transition-colors duration-200
        flex justify-between items-center
        ${className}
      `}
    >
      <span className="font-mono text-sm font-semibold uppercase tracking-wider text-black">
        {name}
      </span>
      <span className="font-mono text-xs text-gray-500 uppercase tracking-wider">
        {formatDate(date)}
      </span>
    </div>
  );
}
